// src/components/MetadataImport.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import { apiFetch } from '../auth/apiFetch';

const POLL_INTERVAL_MS = 2000;

export default function MetadataImport() {
  const [s3Key, setS3Key] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const pollRef = useRef(null);
  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Fetch last import status on mount
  useEffect(() => {
    const fetchLastImport = async () => {
      try {
        const res = await apiFetch(`${apiBaseUrl}/api/last-import-status`);
        if (res.ok) {
          const data = await res.json();
          setLastImport(data);
        }
      } catch (err) {
        // Silently ignore - not critical
      }
    };
    fetchLastImport();
  }, [apiBaseUrl]);

  const pollStatus = async (id) => {
    try {
      const res = await apiFetch(`${apiBaseUrl}/api/import-status/${id}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to fetch status');
      }
      const data = await res.json();
      setStatus(data);

      // Stop polling if job is done
      if (['completed', 'completed_with_errors', 'failed'].includes(data.status)) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setLoading(false);
        // Update last import display
        if (['completed', 'completed_with_errors'].includes(data.status)) {
          setLastImport(data);
        }
      }
    } catch (err) {
      setError(err.message);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!s3Key) return;
    setLoading(true);
    setJobId(null);
    setStatus(null);
    setError(null);

    try {
      const payload = { s3_key: s3Key };
      const res = await apiFetch(`${apiBaseUrl}/api/import-metadata-from-s3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to start import');
      }

      const data = await res.json();
      setJobId(data.job_id);

      // Start polling for status
      pollRef.current = setInterval(() => pollStatus(data.job_id), POLL_INTERVAL_MS);
      // Initial poll
      pollStatus(data.job_id);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'completed_with_errors':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${mins}m ${secs}s`;
  };

  return (
    <Box width="100%" maxWidth={600} textAlign="center">
      <Typography variant="h5" gutterBottom>
        Import Metadata from S3
      </Typography>

      {lastImport && (
        <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
          Last import: {new Date(lastImport.completed_at).toLocaleString()}
          {' '}({lastImport.successful_imports} products)
        </Alert>
      )}

      <TextField
        label="S3 Key"
        placeholder="e.g., imports/products.xml"
        fullWidth
        value={s3Key}
        onChange={(e) => setS3Key(e.target.value)}
        margin="normal"
        disabled={loading}
      />
      <Button variant="contained" color="primary" onClick={handleImport} disabled={loading || !s3Key}>
        {loading ? <CircularProgress size={24} /> : 'Start Import'}
      </Button>

      {jobId && (
        <Box mt={2} textAlign="left">
          <Typography variant="subtitle2" color="textSecondary">
            Job ID: {jobId}
          </Typography>
        </Box>
      )}

      {status && (
        <Box mt={2} textAlign="left">
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1">Status:</Typography>
            <Chip label={status.status} color={getStatusColor(status.status)} size="small" />
          </Box>

          {status.status === 'running' && (
            <Box mb={2}>
              <LinearProgress />
              <Typography variant="body2" color="textSecondary" mt={1}>
                Processing... {status.processed_products} products processed
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 1,
              background: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" fontWeight="bold">S3 Key:</Typography>
            <Typography variant="body2">{status.s3_key}</Typography>

            <Typography variant="body2" fontWeight="bold">Processed:</Typography>
            <Typography variant="body2">{status.processed_products}</Typography>

            <Typography variant="body2" fontWeight="bold">Successful:</Typography>
            <Typography variant="body2" color="success.main">{status.successful_imports}</Typography>

            <Typography variant="body2" fontWeight="bold">Failed:</Typography>
            <Typography variant="body2" color={status.failed_imports > 0 ? 'error.main' : 'inherit'}>
              {status.failed_imports}
            </Typography>

            <Typography variant="body2" fontWeight="bold">Duration:</Typography>
            <Typography variant="body2">{formatDuration(status.duration_seconds)}</Typography>
          </Box>

          {status.recent_errors && status.recent_errors.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="error">
                Recent Errors ({status.recent_errors.length}):
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  background: '#fff3f3',
                  padding: 1,
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                {status.recent_errors.slice(0, 10).map((err, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    [{err.ean}] {err.error}
                  </Typography>
                ))}
                {status.recent_errors.length > 10 && (
                  <Typography variant="body2" color="textSecondary">
                    ... and {status.recent_errors.length - 10} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Box mt={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
    </Box>
  );
}
